<template>
  <v-dialog v-model="isVisible" max-width="460" data-cy="fee-recipient-submit-confirm-dialog">
    <v-card>
      <v-card-title class="text-h6 font-weight-bold">Submit Disbursement to QuickBooks</v-card-title>
      <v-card-text>
        <p class="text-body-2 text-medium-emphasis mb-4">
          This will create a check in QuickBooks for:
        </p>

        <!-- Recipient summary card -->
        <div class="bg-grey-lighten-4 border rounded-lg px-4 py-3 mb-4">
          <div class="text-caption text-medium-emphasis">Recipient</div>
          <div class="text-subtitle-1 font-weight-bold text-grey-darken-4">
            {{ row?.recipientName }}
          </div>
          <div class="text-caption text-disabled">{{ row?.typeLabel }}</div>
          <div class="d-flex justify-space-between align-center mt-3">
            <span class="text-body-2 text-medium-emphasis">Amount</span>
            <span class="text-h6 font-weight-bold text-grey-darken-4 tabular-nums">
              {{ formatCurrency(row?.amount ?? 0) }}
            </span>
          </div>
        </div>

        <p class="text-caption text-disabled mb-0 leading-[1.6]">
          The disbursement status will update to <strong>Submitted</strong> and a QuickBooks check reference
          will be recorded. This action can be undone while the check has not been reconciled.
        </p>
      </v-card-text>
      <v-card-actions class="pa-6">
        <LlSecondaryButton data-cy="fee-recipient-submit-confirm-cancel" @click="cancel">Cancel</LlSecondaryButton>
        <v-spacer />
        <LlSecondaryButton color="primary" data-cy="fee-recipient-submit-confirm-ok" @click="confirm">
          {{ isRetry ? 'Retry Submit' : 'Submit to QuickBooks' }}
        </LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
// Mirrors liens/LienPushConfirmDialog.vue's structure/content — same payee summary
// card + amount + "creates a QuickBooks check" copy — so submitting a fee recipient
// disbursement previews the same way submitting a lien does.
import { ref, computed } from 'vue';
import { formatCurrency } from '@/services/utils';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import type { DisbursementRowView } from '../settlement/composables/useDisbursements';

const isVisible = ref(false);
const row = ref<DisbursementRowView | null>(null);
let resolvePromise: ((value: boolean) => void) | null = null;

const isRetry = computed(() => row.value?.state === 'error');

/** Open the confirmation for a disbursement row; resolves true when confirmed. */
function open(target: DisbursementRowView): Promise<boolean> {
  row.value = target;
  isVisible.value = true;
  return new Promise((resolve) => {
    resolvePromise = resolve;
  });
}

function confirm(): void {
  isVisible.value = false;
  resolvePromise?.(true);
  resolvePromise = null;
}

function cancel(): void {
  isVisible.value = false;
  resolvePromise?.(false);
  resolvePromise = null;
}

defineExpose({ open });
</script>
